import {
  Component,
  ElementRef, HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {StatusMessage} from "../../../model/status-message";
import StatusMessageUtils from "../../../utils/status-message-utils";
import {faAngleDown, faAngleUp} from "@fortawesome/free-solid-svg-icons";
import {CustomerInteraction} from "../../../model/customerInteraction";
import {Property} from "../../../model/property";
import CustomerInteractionUtils from "../../../utils/customer-interaction-utils";
import {Document} from "../../../model/document";

@Component({
  selector: 'app-status-message',
  templateUrl: './status-message.component.html',
  styleUrls: ['./status-message.component.css'],
})
export class StatusMessageComponent implements OnChanges {

  // StatusMessages are already retrieved in the docuemnt-row.component. The status message date is already converted to utc and they are sorted by date.
  @Input() statusMessages: StatusMessage[] = [];
  @Input() customerInteractions: CustomerInteraction[] = [];
  @Input() property: Property;
  @Input() isLoadingStatusMessages: boolean = false;
  @Input() statusMessageInitializationError: boolean = false;
  @Input() isHidden: boolean = false;
  @Input() documents: Document[];

  @ViewChild('tooltipElement', { static: false }) tooltipElement: ElementRef;
  @ViewChild('statusMessageContent', { static: false }) statusMessageContent: ElementRef;
  @ViewChild('historyToggleButton', { static: false }) historyToggleButton: ElementRef;

  hoveredStatusMessage: StatusMessage;
  relevantCustomerInteraction: CustomerInteraction;

  protected readonly StatusMessageUtils = StatusMessageUtils;
  protected readonly faAngleDown = faAngleDown;
  protected readonly faAngleUp = faAngleUp;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the customer interactions input property has changed
    if(changes.customerInteractions){
      // Update the relevantCustomerInteraction
      this.relevantCustomerInteraction = CustomerInteractionUtils.getLatestCustomerInteractionAssociatedWithDocumentIds(this.customerInteractions, this.documents);
    }
    // Check if the isHidden input property has changed
    if(changes.isHidden || changes.isLoadingStatusMessages){
      // setTimeout is used here to make sure the status message component is rendered since its shown/hidden based on the isHidden boolean
      setTimeout(() => {
        this.updateHistoryToggleButtonTopStyle();
      })
    }
  }

  /**
   * Calculates the offset for the history toggle button based on the height of the status message content.
   *
   * @return Calculated offset as a string with 'px' unit.
   */
  public  calculateHistoryToggleButtonTopOffset(): string{
    if(this.statusMessageContent){
      return this.statusMessageContent.nativeElement.offsetHeight + 33 + 'px';
    }
    return '0px';
  }

  /**
   * Updates the top style of the history toggle button based on the calculated top offset.
   */
  public updateHistoryToggleButtonTopStyle(): void {
    const topOffset = this.calculateHistoryToggleButtonTopOffset();
    if (this.historyToggleButton) {
      this.historyToggleButton.nativeElement.style.top = topOffset;
    }
  }

  /**
   * Toggles the visibility of the status message component.
   * Also updates the history toggle button top offset.
   */
  public toggleVisibility(): void {
    this.isHidden = !this.isHidden;
    // setTimeout is used here to make sure the status message component is rendered since its shown/hidden based on the isHidden boolean
    setTimeout(() => {
      this.updateHistoryToggleButtonTopStyle();
    })
  }

  /**
   * Updates this.hoveredStatusMessage based passed statusMessage
   *
   * @param statusMessage StatusMessage object which should be used to update this.hoveredStatusMessage
   */
  public updateHoveredStatusMessage(statusMessage: StatusMessage): void{
    this.hoveredStatusMessage = statusMessage;
  }

  /**
   * Returns the latest expected response date among the documents
   *
   * @return the latest expected response date or null if there are no documents.
   */
  public getLatestExpectedResponseDateForDocuments(): Date | null {
    if (!this.documents || this.documents.length === 0) {
      return null;
    }
    return this.documents.reduce((latest, doc) => {
      return doc.expectedResponseDate > latest ? doc.expectedResponseDate : latest;
    }, this.documents[0].expectedResponseDate);
  }

  /**
   * Updates the position of the history toggle button when the window is resized
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateHistoryToggleButtonTopStyle()
  }
}
